import { useEffect, useRef, useState } from "react";
import type { Account, BacktestAccount } from "@/api/types";
import { isFuturesAccount, providerLabel } from "@/lib/account";
import { isMarketProviderEnabled } from "@/lib/market-provider";
export type AccountLinkPayload = {
  accountId: string;
  cryptoLimit: number | null;
  fiatLimit: number | null;
  slippage: number | null;
  bypass: boolean;
  reduceOnly: boolean;
};

function parseLimit(val: string): number | null {
  const t = val.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function accountLabel(account: Account): string {
  const market = isFuturesAccount(account) ? "Futures" : "Spot";
  return `${account.name} · ${providerLabel(account.provider)} · ${market}`;
}

type Props = {
  open: boolean;
  mode: "add" | "edit";
  accounts: Account[];
  /** Account ids already linked (excluded from add picker). */
  connectedAccountIds?: Set<string>;
  /** Prefer futures/spot when filtering add options. */
  preferFutures?: boolean;
  editingAccount?: BacktestAccount | null;
  onSave: (payload: AccountLinkPayload) => Promise<void>;
  onClose: () => void;
};

export function BacktestAccountModal({
  open,
  mode,
  accounts,
  connectedAccountIds = new Set(),
  preferFutures,
  editingAccount = null,
  onSave,
  onClose,
}: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [bypass, setBypass] = useState(false);
  const [reduceOnly, setReduceOnly] = useState(false);
  const [cryptoLimitStr, setCryptoLimitStr] = useState("");
  const [fiatLimitStr, setFiatLimitStr] = useState("");
  const [slippageStr, setSlippageStr] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setSaving(false);
    if (mode === "edit" && editingAccount) {
      setSelectedAccountId(editingAccount.account_id);
      setBypass(Boolean(editingAccount.bypass));
      setReduceOnly(Boolean(editingAccount.reduce_only));
      setCryptoLimitStr(editingAccount.crypto_limit != null ? String(editingAccount.crypto_limit) : "");
      setFiatLimitStr(editingAccount.fiat_limit != null ? String(editingAccount.fiat_limit) : "");
      setSlippageStr(editingAccount.slippage != null ? String(editingAccount.slippage) : "");
    } else {
      setSelectedAccountId("");
      setBypass(false);
      setReduceOnly(false);
      setCryptoLimitStr("");
      setFiatLimitStr("");
      setSlippageStr("");
    }
  }, [open, mode, editingAccount]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const availableAccounts = accounts.filter((a) => {
    if (connectedAccountIds.has(a.id)) return false;
    if (!a.enabled) return false;
    if (!isMarketProviderEnabled(a.provider)) return false;
    if (preferFutures === undefined) return true;
    return isFuturesAccount(a) === preferFutures;
  });

  const editingName =
    accounts.find((a) => a.id === editingAccount?.account_id)?.name ||
    editingAccount?.account_id ||
    "Unknown";

  async function handleSubmit() {
    setError("");
    const accountId = mode === "add" ? selectedAccountId : editingAccount?.account_id || "";
    if (!accountId) {
      setError("Please select an account");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        accountId,
        cryptoLimit: bypass ? null : parseLimit(cryptoLimitStr),
        fiatLimit: bypass ? null : parseLimit(fiatLimitStr),
        slippage: bypass ? null : parseLimit(slippageStr),
        bypass,
        reduceOnly,
      });
      onClose();
    } catch (e: unknown) {
      setError((e instanceof Error && e.message) || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  const choice = (on: boolean, onClick: () => void, title: string, hint: string) => (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`flex flex-col gap-1.5 rounded-[12px] border bg-[var(--bg-card-elevated)] p-3 text-left ${
        on ? "border-[var(--primary)] shadow-[inset_0_0_0_1px_var(--primary)]" : "border-[var(--border)] hover:bg-[var(--bg-hover)]"
      }`}
    >
      <b className="text-[13px] font-semibold text-[var(--text-primary)]">{title}</b>
      <span className="text-xs text-[var(--text-muted)]">{hint}</span>
    </button>
  );
  const label = "text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="backtest-account-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        className="flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[14px] border border-[var(--border)] bg-[var(--bg-card)] shadow-[var(--shadow-lg)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[var(--divider)] px-4 py-3">
          <h2 id="backtest-account-modal-title" className="m-0 text-[15px] font-semibold text-[var(--text-primary)]">
            {mode === "add" ? "Add an exchange" : `Edit ${editingName}`}
          </h2>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="flex flex-col gap-3.5 overflow-y-auto p-4">
          {error && <div className="rounded-[10px] bg-[var(--danger-bg)] px-3 py-2 text-[13px] text-[var(--danger)]">{error}</div>}

          {mode === "add" && (
            <label className="flex flex-col gap-1.5">
              <span className={label}>Exchange</span>
              {availableAccounts.length === 0 ? (
                <span className="text-[13px] text-[var(--text-muted)]">No other exchange fits this strategy.</span>
              ) : (
                <select value={selectedAccountId} onChange={(e) => setSelectedAccountId(e.target.value)}>
                  <option value="">Select an exchange…</option>
                  {availableAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {accountLabel(a)}
                    </option>
                  ))}
                </select>
              )}
            </label>
          )}

          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {choice(bypass, () => setBypass(true), "Script's order size", "Orders use the size the strategy computes. Set the exchange leverage to match.")}
            {choice(!bypass, () => setBypass(false), "Cap what it can use", "Limit the money and coins this strategy may trade with.")}
          </div>

          {!bypass && (
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <label className="flex flex-col gap-1.5">
                <span className={label}>Money limit</span>
                <input inputMode="decimal" value={fiatLimitStr} onChange={(e) => setFiatLimitStr(e.target.value)} placeholder="No limit" />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className={label}>Coin limit</span>
                <input inputMode="decimal" value={cryptoLimitStr} onChange={(e) => setCryptoLimitStr(e.target.value)} placeholder="No limit" />
              </label>
              <label className="flex flex-col gap-1.5 sm:col-span-2">
                <span className={label}>Max slippage %</span>
                <input inputMode="decimal" value={slippageStr} onChange={(e) => setSlippageStr(e.target.value)} placeholder="Market orders" />
                <span className="text-xs text-[var(--text-muted)]">
                  Empty sends market orders. With a value, entries are limit orders within ± this % of the signal price.
                </span>
              </label>
            </div>
          )}

          <button
            type="button"
            role="switch"
            aria-checked={reduceOnly}
            onClick={() => setReduceOnly((v) => !v)}
            className="flex items-start gap-3 rounded-[12px] border border-[var(--border)] p-3 text-left"
          >
            <span
              className={`relative mt-0.5 h-5 w-[34px] shrink-0 rounded-full border ${
                reduceOnly ? "border-[var(--primary)] bg-[var(--primary)]" : "border-[var(--border)] bg-[var(--bg-hover)]"
              }`}
            >
              <i className={`absolute top-[2px] h-3.5 w-3.5 rounded-full ${reduceOnly ? "left-[16px] bg-white" : "left-[2px] bg-[var(--text-muted)]"}`} />
            </span>
            <span>
              <b className="block text-[13px] font-semibold text-[var(--text-primary)]">Reduce-only exits</b>
              <span className="text-xs text-[var(--text-muted)]">Exits can never flip the position by accident. Futures only.</span>
            </span>
          </button>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-[var(--divider)] px-4 py-3">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || (mode === "add" && !selectedAccountId)}
            onClick={() => void handleSubmit()}
          >
            {saving ? "Saving…" : mode === "add" ? "Add" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
